import React, {
	useCallback,
	type KeyboardEvent,
	useImperativeHandle,
	useState,
	useEffect,
} from 'react';
import isEqual from 'lodash/isEqual';
import GoalsPicker, { type Goal } from '@atlassian/jira-business-goal-picker';
import { ff } from '@atlassian/jira-feature-flagging';
import { useGoalsField } from '@atlassian/jira-issue-field-goals';
import { GOALS_CF_TYPE } from '@atlassian/jira-platform-field-config';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { CellWrapper } from '../../../common/cell-wrapper';
import type { TransformedGoals } from '../../../common/field-types';
import type { FieldCellProps, BasicListViewItem } from '../../../common/types';
import { AssociateFieldPopup } from './associate-field-popup';
import { useIsFieldAssociatedWithIssueType } from './utils';

const defaultValue = { goals: [], totalCount: 0 };

export const GoalsCell = ({
	rowData: issue,
	cellData: field,
	editMode,
	setEditMode,
	fieldId,
	fieldName,
	cellRef,
}: FieldCellProps<BasicListViewItem, TransformedGoals>) => {
	const [goalsValue, setGoalsValue] = useState<TransformedGoals['value']>(
		field?.value ?? defaultValue,
	);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const isEditable = field?.isEditable ?? true;

	const isFieldAssociated = useIsFieldAssociatedWithIssueType({
		fieldId,
		issueTypeId: issue.issuetype?.id,
	});

	const [{ value }, { saveValue }] = useGoalsField({
		issueId: issue.issueId,
		issueKey: issue.key,
		fieldKey: fieldId,
		initialValue: field?.value ?? defaultValue,
	});

	useEffect(() => {
		setGoalsValue(field?.value ?? defaultValue);
	}, [field?.value]);

	if (ff('hrz-716-integrate-goal-picker-into-issue-view') && !isEqual(value, goalsValue)) {
		setGoalsValue(value);
	}

	const onClose = useCallback(() => {
		setEditMode(false);
	}, [setEditMode]);

	const handleKeyDown = useCallback((event: KeyboardEvent<HTMLElement>) => {
		if (event.code === 'Tab') {
			return true;
		}

		return false;
	}, []);

	const cellOverrides = useCallback(
		() => ({
			onKeyDown: handleKeyDown,
		}),
		[handleKeyDown],
	);

	useImperativeHandle(cellRef, cellOverrides, [cellOverrides]);

	const onGoalPicked = useCallback(
		(goal: Goal) => {
			const newValue = {
				goals: [...goalsValue.goals, goal],
				totalCount: goalsValue.totalCount + 1,
			};

			setGoalsValue(newValue);

			if (ff('hrz-716-integrate-goal-picker-into-issue-view')) {
				saveValue(newValue, null, createAnalyticsEvent({}));
			}
		},
		[createAnalyticsEvent, goalsValue.goals, goalsValue.totalCount, saveValue],
	);

	const onGoalUnlinked = useCallback(
		(_: string, remainingGoals: Goal[]) => {
			const newValue = {
				goals: remainingGoals,
				totalCount: goalsValue.totalCount > 0 ? goalsValue.totalCount - 1 : 0,
			};
			setGoalsValue(newValue);

			if (ff('hrz-716-integrate-goal-picker-into-issue-view')) {
				saveValue(newValue, null, createAnalyticsEvent({}));
			}
		},
		[createAnalyticsEvent, goalsValue.totalCount, saveValue],
	);

	return (
		<CellWrapper isEditable={isEditable}>
			{isFieldAssociated && (
				<GoalsPicker
					issueAri={issue.id}
					editMode={editMode}
					goals={goalsValue.goals}
					goalCount={goalsValue.totalCount}
					onClose={onClose}
					isEditable={isEditable}
					onGoalPicked={onGoalPicked}
					onGoalUnlinked={onGoalUnlinked}
				/>
			)}
			{!isFieldAssociated && (
				<AssociateFieldPopup
					isOpen={editMode}
					issueType={issue.issuetype?.name}
					issueTypeId={issue.issuetype?.id}
					fieldId={fieldId}
					fieldName={fieldName}
					onClose={onClose}
				/>
			)}
		</CellWrapper>
	);
};

export const goalsCellHandler = {
	[GOALS_CF_TYPE]: GoalsCell,
};
